import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MeditationResults, MeditationResultItem } from '../components/meditation';
import { config } from '../config';
import { authService } from '../services/auth';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

const AVAILABLE_TAGS = [
  'Sitting', 'Lying', 'Walking', 'Eyes open', 'Eyes closed', 'Relaxing',
  'Thinking', 'Focus', 'Sleeping', 'Dreaming', 'Out of body', 'Spiritual', 'Nature',
  'Non-Spiritual', 'Body Scan', 'Story', 'Manifestation', 'Visualization',
  'Affirmations', 'Mantras', 'Healing', 'Music','Ambient','Instrumental',
  'Delta', 'Theta', 'Alpha', 'Beta','Gamma', 
  'Binaural Beats', 'Amplitude Modulation', 'Frequency Modulation',
  'Stereo Modulation', 'Isochronic Tones'
];

const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: white;
`;

const SearchContainer = styled.div`
  margin-bottom: 40px;
`;

const SearchRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;


const Filters = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  
`;

const Label = styled.label`
  color: #666;
  font-size: 0.9rem;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #666;
  border-radius: 4px;
  background-color: #333;
  color: #cccccc;
  font-size: 0.9rem;
`;


const TagsSection = styled.div`
  margin-top: 20px;
  margin-bottom: 30px;
`;

const TagsLabel = styled.h3`
  color: #ffffff;
  margin-bottom: 12px;
  font-size: 1rem;
  font-weight: 600;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.button<{ $selected: boolean }>`
  background: ${props => props.$selected ? '#007bff' : '#333'};
  color: white;
  border: 1px solid ${props => props.$selected ? '#007bff' : '#444'};
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$selected ? '#0056b3' : '#444'};
  }
`;


const ResultsSection = styled.div`
  margin-top: 40px;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  padding: 60px 20px;
`;

const InfiniteScrollSentinel = styled.div`
  height: 20px;
  margin: 20px 0;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #999;
  font-size: 1rem;
`;


const Unguided: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [type, setType] = useState('unguided');
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<MeditationResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [maxResults] = useState(100);
  const [offset, setOffset] = useState(0);

  // Parse URL parameters on mount and restore search state
  useEffect(() => {
    const keyword = searchParams.get('keyword') || '';
    const urlType = searchParams.get('type') || 'all';
    const urlDuration = searchParams.get('duration') || 'all';
    const urlSort = searchParams.get('sort') || 'date_desc';
    const urlTags = searchParams.get('tags');
    const urlTagsArray = urlTags ? urlTags.split(',').filter(tag => AVAILABLE_TAGS.includes(tag)) : [];
    const urlLoaded = parseInt(searchParams.get('loaded') || '0', 10);


    setSortBy(urlSort);
    setSelectedTags(urlTagsArray);

   
      //automatically start search or restore search results
      setHasSearched(true);
      if (urlLoaded > 0) {
        // Restore previous search results by loading all batches up to urlLoaded
        restoreSearchResults(keyword, urlType, urlDuration, urlTagsArray, urlLoaded, urlSort);
      } else {
        // Start fresh search
        performInitialSearch('unguided', urlSort, urlTagsArray);
      }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount



  const handleTagToggle = (tag: string) => {
    const newTags = selectedTags.includes(tag) 
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    
    setSelectedTags(newTags);
    // Pass the new tags directly to the search function
    performInitialSearch(type, sortBy, newTags);
  };

  // Helper function to build API parameters
  const buildApiParams = (searchType: string, sortOrder: string, tags: string[], currentOffset: number) => {
    const apiParams = new URLSearchParams();
    
    
    if (searchType !== 'all') {
      apiParams.append('is_unguided', searchType === 'unguided' ? 'true' : 'false');
    }
        
    if (sortOrder !== 'date_desc') {
      apiParams.append('sort', sortOrder);
    }
    
    if (tags.length > 0) {
      apiParams.append('tags', tags.join(','));
    }

    apiParams.append('offset', currentOffset.toString());
    apiParams.append('limit', '20');

    return apiParams;
  };

  // Function to update URL parameters
  const updateURLParams = (searchType: string, sortOrder: string, tags: string[], loaded?: number) => {
    const newParams = new URLSearchParams();
    
    
    if (searchType !== 'all') {
      newParams.set('type', searchType);
    }
    
    
    if (sortOrder !== 'date_desc') {
      newParams.set('sort', sortOrder);
    }
    
    if (tags.length > 0) {
      newParams.set('tags', tags.join(','));
    }

    if (loaded && loaded > 0) {
      newParams.set('loaded', loaded.toString());
    }

    setSearchParams(newParams);
  };

  // Initial search function (resets results)
  const performInitialSearch = useCallback(async (searchType: string, sortOrder: string, tags: string[]) => {
    setLoading(true);
    setSearchResults([]);
    setOffset(0);
    setTotalLoaded(0);
    setHasMore(true);

    try {
      const apiParams = buildApiParams(searchType, sortOrder, tags, 0);
      const response = await fetch(`${config.apiUrl}/scripts/search?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      setSearchResults(meditations);
      setOffset(meditations.length);
      setTotalLoaded(meditations.length);
      setHasMore(data.hasMore && meditations.length < maxResults);

      // Update URL with search results
      updateURLParams(searchType, sortOrder, tags, meditations.length);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [maxResults, setSearchParams]);

  // Load more results function for infinite scroll
  const loadMoreResults = useCallback(async () => {
    if (loading || !hasMore || totalLoaded >= maxResults) return;

    setLoading(true);

    try {
      const apiParams = buildApiParams(type, sortBy, selectedTags, offset);
      const response = await fetch(`${config.apiUrl}/scripts/search?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const newMeditations = data.meditations || [];
      const newTotalLoaded = totalLoaded + newMeditations.length;
      
      setSearchResults(prev => [...prev, ...newMeditations]);
      setOffset(prev => prev + newMeditations.length);
      setTotalLoaded(newTotalLoaded);
      setHasMore(data.hasMore && newTotalLoaded < maxResults);

      // Update URL with new loaded count
      updateURLParams(type, sortBy, selectedTags, newTotalLoaded);
    } catch (error) {
      console.error('Load more error:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, totalLoaded, maxResults, offset, type, sortBy, selectedTags]);

  // Restore search results when returning from a meditation page
  const restoreSearchResults = useCallback(async (keyword: string, searchType: string, searchDuration: string, tags: string[], targetLoaded: number, sortOrder: string = 'date_desc') => {
    setLoading(true);
    setSearchResults([]);
    setOffset(0);
    setTotalLoaded(0);

    try {
      // Load all results up to targetLoaded in one request
      const apiParams = buildApiParams(searchType, sortOrder, tags, 0);
      apiParams.set('limit', Math.min(targetLoaded, maxResults).toString());
      
      const response = await fetch(`${config.apiUrl}/scripts/search?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      setSearchResults(meditations);
      setOffset(meditations.length);
      setTotalLoaded(meditations.length);
      setHasMore(data.hasMore && meditations.length < maxResults);
    } catch (error) {
      console.error('Restore search error:', error);
      setSearchResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [maxResults]);

  // Set up infinite scroll
  const { sentinelRef, loadingRef } = useInfiniteScroll({
    loadMore: loadMoreResults,
    hasMore: hasMore && totalLoaded < maxResults,
    loading: loading
  });

  return (
    <Container>
      
      <SearchContainer>
        <SearchRow>
          <Title>Unguided Meditations</Title>

          <Filters>


            <FilterGroup>
              <Label>Sort By:</Label>
              <Select value={sortBy} onChange={(e) => {
                const newSortBy = e.target.value;
                setSortBy(newSortBy);
                performInitialSearch(type, newSortBy, selectedTags);
              }}>
                <option value="date_desc">Date 📉</option>
                <option value="date_asc">Date 📈</option>
                <option value="views_desc">Views 📉</option>
                <option value="views_asc">Views 📈</option>
                <option value="ratings_desc">Ratings 📉</option>
                <option value="ratings_asc">Ratings 📈</option>
              </Select>
            </FilterGroup>
          </Filters>
        </SearchRow>

        <TagsSection>
          <TagsLabel>Tags (select to narrow down results):</TagsLabel>
          <TagsContainer>
            {AVAILABLE_TAGS.map(tag => (
              <Tag
                key={tag}
                type="button"
                $selected={selectedTags.includes(tag)}
                onClick={() => handleTagToggle(tag)}
              >
                {tag}
              </Tag>
            ))}
          </TagsContainer>
        </TagsSection>

       
      </SearchContainer>

      <ResultsSection>
        {!hasSearched ? (
          <NoResultsMessage>
            
          </NoResultsMessage>
        ) : (
          <>
           
            
            <MeditationResults 
              meditations={searchResults} 
              loading={false} // Don't show loading state in results grid
            />

            {/* Infinite scroll loading indicator */}
            {loading && searchResults.length > 0 && (
              <LoadingIndicator ref={loadingRef}>
                Loading more meditations...
              </LoadingIndicator>
            )}

            {/* Infinite scroll sentinel */}
            {hasMore && totalLoaded < maxResults && !loading && (
              <InfiniteScrollSentinel ref={sentinelRef} />
            )}

            
          </>
        )}
      </ResultsSection>
    </Container>
  );
};

export default Unguided; 